import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { usePaqueteDetail } from '@/lib/hooks/use-paquetes';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { EstadoPaqueteBadge } from '@/components/paquetes/estado-badge';
import { PinDisplay } from '@/components/paquetes/pin-display';
import { RetirarDialog } from '@/components/paquetes/retirar-dialog';
import { DevolverDialog } from '@/components/paquetes/devolver-dialog';
import { AnularDialog } from '@/components/paquetes/anular-dialog';
import { Button } from '@/components/ui/button';
import type { Paquete } from '@condo/contracts';

type DialogState =
  | { type: 'retirar' }
  | { type: 'devolver' }
  | { type: 'anular' }
  | null;

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Full detail panel for admin/conserje/comite — shows all fields and action buttons. */
function ConserjeDetail({
  paquete,
  condoId,
}: {
  paquete: Paquete;
  condoId: string;
}) {
  const [dialog, setDialog] = useState<DialogState>(null);
  const isPending = paquete.estado === 'recibido';

  return (
    <>
      <PageHeader
        eyebrow={`Encomiendas · Unidad ${paquete.unidadNumero}`}
        title={paquete.destinatarioNombre}
        action={
          <div className="flex items-center gap-2">
            <EstadoPaqueteBadge estado={paquete.estado} />
            {isPending && (
              <>
                <Button
                  size="sm"
                  onClick={() => setDialog({ type: 'retirar' })}
                >
                  Marcar retirado
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ type: 'devolver' })}
                >
                  Devolver
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-[var(--color-danger)]"
                  onClick={() => setDialog({ type: 'anular' })}
                >
                  Anular
                </Button>
              </>
            )}
          </div>
        }
        ruleSpacing="mb-8"
      />

      <dl className="grid grid-cols-2 gap-x-8 gap-y-5 text-sm sm:grid-cols-3">
        <div>
          <dt className="eyebrow">Descripcion</dt>
          <dd className="mt-1">{paquete.descripcion ?? '—'}</dd>
        </div>
        <div>
          <dt className="eyebrow">Remitente</dt>
          <dd className="mt-1">{paquete.remitente ?? '—'}</dd>
        </div>
        <div>
          <dt className="eyebrow">Tracking</dt>
          <dd className="mt-1 font-mono">{paquete.trackingNumber ?? '—'}</dd>
        </div>
        <div>
          <dt className="eyebrow">Registrado por</dt>
          <dd className="mt-1">{paquete.registradoPorNombre}</dd>
        </div>
        <div>
          <dt className="eyebrow">Registrado el</dt>
          <dd className="mt-1">{fmt(paquete.registradoAt)}</dd>
        </div>
        <div>
          <dt className="eyebrow">Unidad</dt>
          <dd className="mt-1">
            <Link
              to={`/app/condominios/${condoId}/unidades/${paquete.unidadId}`}
              className="text-[var(--color-primary)] underline-offset-2 hover:underline"
            >
              {paquete.unidadNumero}
            </Link>
          </dd>
        </div>

        {paquete.retiradoAt && (
          <>
            <div>
              <dt className="eyebrow">Retirado por</dt>
              <dd className="mt-1">{paquete.retiradoPorNombre ?? '—'}</dd>
            </div>
            <div>
              <dt className="eyebrow">Retirado el</dt>
              <dd className="mt-1">{fmt(paquete.retiradoAt)}</dd>
            </div>
            {paquete.retiradoSinPin && (
              <div>
                <dt className="eyebrow">Sin PIN — observaciones</dt>
                <dd className="mt-1">{paquete.retiradoObservaciones ?? '—'}</dd>
              </div>
            )}
          </>
        )}

        {paquete.devueltoAt && (
          <>
            <div>
              <dt className="eyebrow">Devuelto el</dt>
              <dd className="mt-1">{fmt(paquete.devueltoAt)}</dd>
            </div>
            <div>
              <dt className="eyebrow">Razon devolucion</dt>
              <dd className="mt-1">{paquete.devueltoRazon ?? '—'}</dd>
            </div>
          </>
        )}

        {paquete.anuladoAt && (
          <>
            <div>
              <dt className="eyebrow">Anulado el</dt>
              <dd className="mt-1">{fmt(paquete.anuladoAt)}</dd>
            </div>
            <div>
              <dt className="eyebrow">Razon anulacion</dt>
              <dd className="mt-1">{paquete.anuladoRazon ?? '—'}</dd>
            </div>
          </>
        )}
      </dl>

      {dialog?.type === 'retirar' && (
        <RetirarDialog
          paqueteId={paquete.id}
          destinatarioNombre={paquete.destinatarioNombre}
          open
          onOpenChange={(open) => { if (!open) setDialog(null); }}
        />
      )}
      {dialog?.type === 'devolver' && (
        <DevolverDialog
          paqueteId={paquete.id}
          destinatarioNombre={paquete.destinatarioNombre}
          open
          onOpenChange={(open) => { if (!open) setDialog(null); }}
        />
      )}
      {dialog?.type === 'anular' && (
        <AnularDialog
          paqueteId={paquete.id}
          destinatarioNombre={paquete.destinatarioNombre}
          open
          onOpenChange={(open) => { if (!open) setDialog(null); }}
        />
      )}
    </>
  );
}

/** Simplified view for residente/propietario — shows status and PIN if pending. */
function ResidenteDetail({ paquete }: { paquete: Paquete }) {
  const isPending = paquete.estado === 'recibido';

  return (
    <>
      <PageHeader
        eyebrow={`Encomiendas · Unidad ${paquete.unidadNumero}`}
        title={paquete.descripcion ?? 'Paquete'}
        action={<EstadoPaqueteBadge estado={paquete.estado} />}
        ruleSpacing="mb-8"
      />

      {isPending && (
        <div className="mb-6 max-w-xs">
          <PinDisplay pin={paquete.pin} />
          <p className="mt-2 text-center text-xs text-[var(--color-ink-muted)]">
            Presenta este PIN en porteria al retirar.
          </p>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-8 gap-y-5 text-sm">
        <div>
          <dt className="eyebrow">Descripcion</dt>
          <dd className="mt-1">{paquete.descripcion ?? '—'}</dd>
        </div>
        <div>
          <dt className="eyebrow">Remitente</dt>
          <dd className="mt-1">{paquete.remitente ?? '—'}</dd>
        </div>
        {paquete.trackingNumber && (
          <div>
            <dt className="eyebrow">Tracking</dt>
            <dd className="mt-1 font-mono">{paquete.trackingNumber}</dd>
          </div>
        )}
        <div>
          <dt className="eyebrow">Registrado el</dt>
          <dd className="mt-1">{fmt(paquete.registradoAt)}</dd>
        </div>
        {paquete.retiradoAt && (
          <div>
            <dt className="eyebrow">Retirado el</dt>
            <dd className="mt-1">{fmt(paquete.retiradoAt)}</dd>
          </div>
        )}
        {paquete.devueltoAt && (
          <div>
            <dt className="eyebrow">Devuelto el</dt>
            <dd className="mt-1">{fmt(paquete.devueltoAt)}</dd>
          </div>
        )}
      </dl>
    </>
  );
}

/**
 * Paquete detail page with role dispatch.
 * - admin / conserje / comite / super_admin: full view + action buttons.
 * - residente / propietario: PIN + status (PIN visible only while estado === 'recibido').
 */
export function PaqueteDetailRoute() {
  const { id } = useParams<{ id?: string }>();
  const { rol, condoId } = useCondoContext();
  const { data, isLoading, isError } = usePaqueteDetail(id);

  const backTo = `/app/condominios/${condoId}/paquetes`;

  if (isLoading) {
    return (
      <PageContainer width="wide">
        <div className="py-16 text-center text-sm text-[var(--color-ink-muted)]">
          Cargando paquete…
        </div>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer width="wide">
        <div className="py-16 text-center text-sm text-[var(--color-danger)]">
          No se pudo cargar el paquete.
        </div>
      </PageContainer>
    );
  }

  const isStaff =
    rol === 'admin' ||
    rol === 'conserje' ||
    rol === 'comite' ||
    rol === 'super_admin';

  return (
    <PageContainer width="wide">
      <Button variant="ghost" asChild size="sm" className="mb-6">
        <Link to={backTo}>
          <ArrowLeft className="h-4 w-4" />
          Volver a Paquetes
        </Link>
      </Button>

      {isStaff ? (
        <ConserjeDetail paquete={data} condoId={condoId} />
      ) : (
        <ResidenteDetail paquete={data} />
      )}
    </PageContainer>
  );
}
